import { ReactNode, useRef, useState, useEffect } from 'react';
import { Box, Flex, IconButton, Icon, Text, Heading, Spinner } from '@chakra-ui/react';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import FilterChips, { FilterOption } from './filter-chips';

type Props = {
  title: string;
  filters?: FilterOption[];
  filterValue?: string;
  onFilterChange?: (value: string) => void;
  primaryFilters?: FilterOption[];
  primaryFilterValue?: string;
  onPrimaryFilterChange?: (value: string) => void;
  isLoading?: boolean;
  emptyMessage?: string;
  emptyState?: ReactNode;
  children: ReactNode;
};

const CARD_WIDTH = 280;

// DS-style carousel arrow: a circular surface button floating over the edge of
// the card row, vertically centered. Rendered only when that direction can
// scroll. Offsets stay inside the page gutter on mobile so the button doesn't
// spill past the viewport and cause horizontal page scroll.
function EdgeArrow({ side, onClick }: { side: 'left' | 'right'; onClick: () => void }) {
  return (
    <IconButton
      aria-label={side === 'left' ? 'Scroll left' : 'Scroll right'}
      position='absolute'
      {...(side === 'left'
        ? { left: { base: '-8px', md: '-20px' } }
        : { right: { base: '-8px', md: '-20px' } })}
      top='50%'
      transform='translateY(-50%)'
      zIndex={2}
      variant='outline'
      boxSize='40px'
      minW='40px'
      p={0}
      bg='bg.panel'
      color='fg.strong'
      borderColor='border.emphasized'
      borderRadius='full'
      shadow='md'
      onClick={onClick}
      _hover={{ bg: 'fg.strong', color: 'white', borderColor: 'fg.strong' }}
    >
      <Icon
        as={side === 'left' ? ChevronLeftIcon : ChevronRightIcon}
        w={5}
        h={5}
        strokeWidth={2.5}
      />
    </IconButton>
  );
}

export default function ContentCarousel({
  title,
  filters,
  filterValue,
  onFilterChange,
  primaryFilters,
  primaryFilterValue,
  onPrimaryFilterChange,
  isLoading,
  emptyMessage = 'Nothing to show yet.',
  emptyState,
  children,
}: Props) {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollState = () => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 4);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  };

  useEffect(() => {
    updateScrollState();
    const el = scrollRef.current;
    if (!el) return;
    el.addEventListener('scroll', updateScrollState, { passive: true });
    window.addEventListener('resize', updateScrollState);
    return () => {
      el.removeEventListener('scroll', updateScrollState);
      window.removeEventListener('resize', updateScrollState);
    };
  }, [children]);

  const scrollBy = (direction: -1 | 1) => {
    const el = scrollRef.current;
    if (!el) return;
    const distance = direction * (CARD_WIDTH * 2);
    el.scrollBy({ left: distance, behavior: 'smooth' });
  };

  const childArray = Array.isArray(children) ? children : [children];
  const isEmpty = !isLoading && childArray.flat().filter(Boolean).length === 0;
  const showFilters =
    filters && filters.length > 0 && filterValue !== undefined && onFilterChange;
  const showPrimaryFilters =
    primaryFilters &&
    primaryFilters.length > 0 &&
    primaryFilterValue !== undefined &&
    onPrimaryFilterChange;

  const itemCount = childArray.flat().filter(Boolean).length;

  return (
    <Box mb={8}>
      <Flex align='baseline' gap={2} mb={3}>
        <Heading
          as='h2'
          textStyle='display'
          fontSize={{ base: 'h3', md: 'h2' }}
        >
          {title}
        </Heading>
        {itemCount > 0 && (
          <Text fontSize='sm' fontWeight='medium' color='fg.subtle'>
            {itemCount}
          </Text>
        )}
      </Flex>
      {showPrimaryFilters && (
        <Box mb={2}>
          <FilterChips
            options={primaryFilters!}
            value={primaryFilterValue!}
            onChange={onPrimaryFilterChange!}
          />
        </Box>
      )}
      {showFilters && (
        <Box mb={3}>
          <FilterChips options={filters!} value={filterValue!} onChange={onFilterChange!} />
        </Box>
      )}
      {isLoading && (
        <Flex align='center' justify='center' py={6}>
          <Spinner size='sm' color='fg.subtle' />
        </Flex>
      )}
      {isEmpty && !isLoading && (
        <>
          {emptyState ?? (
            <Box py={8} textAlign='center'>
              <Text fontSize='sm' color='fg.muted'>
                {emptyMessage}
              </Text>
            </Box>
          )}
        </>
      )}
      {!isLoading && !isEmpty && (
        <Box position='relative'>
          {canScrollLeft && (
            <EdgeArrow side='left' onClick={() => scrollBy(-1)} />
          )}
          {canScrollRight && (
            <EdgeArrow side='right' onClick={() => scrollBy(1)} />
          )}
          <Flex
            ref={scrollRef}
            gap={4}
            overflowX='auto'
            alignItems='stretch'
            scrollSnapType='x mandatory'
            pb={2}
            css={{
              scrollbarWidth: 'none',
              '&::-webkit-scrollbar': { display: 'none' },
            }}
          >
            {childArray.flat().map((child, idx) =>
              child ? (
                <Box
                  key={idx}
                  flex='0 0 auto'
                  width={`${CARD_WIDTH}px`}
                  scrollSnapAlign='start'
                  display='flex'
                >
                  {child}
                </Box>
              ) : null,
            )}
          </Flex>
        </Box>
      )}
    </Box>
  );
}
